import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { CONTRACT_STATUS_OPTIONS, RECORDS_PER_PAGE_OPTIONS } from '../../../../utils/constants';

import Dropdown from '../../../common/inputs/dropdown/Dropdown';
import Button from '../../../common/button/Button';
import Table from '../../../common/table/Table';
import DatePicker from '../../../common/inputs/date-picker/DatePicker';
import TextInput from '../../../common/inputs/text-input/TextInput';

import './deal-room-management.scss';

const DealRoomManagement = ({
  contracts,
  products,
  facilitators,
  initialFilters,
  currentPage,
  totalPages,
  retrieveContracts,
}) => {
  const [recordsPerPage, setRecordsPerPage] = useState({ label: 20, value: 20 });
  const [status, setStatus] = useState(undefined);
  const [product, setProduct] = useState(undefined);
  const [facilitator, setFacilitator] = useState(undefined);
  const [minimumPrice, setMinimumPrice] = useState('');
  const [maximumPrice, setMaximumPrice] = useState('');
  const [initiatedFrom, setInitiatedFrom] = useState(undefined);
  const [initiatedTo, setInitiatedTo] = useState(undefined);

  const filters = useRef({});

  const navigate = useNavigate();

  useEffect(() => {
    setRecordsPerPage(initialFilters.recordsPerPage);
    setStatus(initialFilters.status);
    setProduct(initialFilters.product);
    setFacilitator(initialFilters.facilitator);
    setMinimumPrice(initialFilters.minimumPrice);
    setMaximumPrice(initialFilters.maximumPrice);
    setInitiatedFrom(initialFilters.initiatedFrom);
    setInitiatedTo(initialFilters.initiatedTo);

    filters.current = initialFilters;
  }, [initialFilters]);

  const handleRowClick = (contract) => {
    navigate(`/admin/deal-room-management/${contract.externalReference}`);
  };

  const handleMinPriceChange = (minPrice) => {
    setMinimumPrice(minPrice.target.value);
  };

  const handleMaxPriceChange = (maxPrice) => {
    setMaximumPrice(maxPrice.target.value);
  };

  const haveFiltersChanged = () => {
    let definedKeys = 0;
    for (let key in filters.current) {
      if (filters.current[key] !== undefined && filters.current[key] !== null && filters.current[key] !== '') {
        definedKeys++;
      }
    }

    if (filters.current.minimumPrice !== undefined || filters.current.maximumPrice !== undefined) {
      return true;
    }

    if (filters.current.page !== 0 || filters.current.recordsPerPage.value !== 20 || definedKeys !== 2) {
      return true;
    }

    return false;
  };

  const applyFilters = (page) => {
    filters.current = {
      recordsPerPage,
      page,
      status,
      product,
      facilitator,
      minimumPrice,
      maximumPrice,
      initiatedFrom,
      initiatedTo,
    };

    retrieveContracts(filters.current);
  };

  const removeFilters = () => {
    const recordsPerPage = RECORDS_PER_PAGE_OPTIONS[1];

    filters.current = {
      page: 0,
      recordsPerPage,
    };

    setRecordsPerPage(recordsPerPage);
    setStatus(undefined);
    setProduct(undefined);
    setFacilitator(undefined);
    setMinimumPrice('');
    setMaximumPrice('');
    setInitiatedFrom(null);
    setInitiatedTo(null);

    retrieveContracts(filters.current);
  };

  return (
    <section className="deal-room-management">
      <section className="deal-room-management__filter" data-testid="deal-room-management-filters">
        <section className="deal-room-management__filter__filters">
          <p>Filter</p>

          <label htmlFor="records-per-page">Records per Page</label>
          <Dropdown
            name="records-per-page"
            value={recordsPerPage}
            options={RECORDS_PER_PAGE_OPTIONS}
            onChange={(option) => setRecordsPerPage(option)}
          />

          <label htmlFor="contract-status">Status</label>
          <Dropdown
            name="contract-status"
            value={status}
            options={CONTRACT_STATUS_OPTIONS}
            onChange={(option) => setStatus(option)}
          />

          <label htmlFor="product">Product</label>
          <Dropdown
            name="product"
            value={product}
            options={products}
            onChange={(option) => setProduct(option)}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.externalReference}
          />

          <label htmlFor="facilitator">Facilitator</label>
          <Dropdown
            name="facilitator"
            value={facilitator}
            options={facilitators}
            onChange={(option) => setFacilitator(option)}
            getOptionLabel={(option) => option.personName}
            getOptionValue={(option) => option.personExternalReference}
          />

          <p>Price</p>
          <div className="deal-room-management__filter__filters__price-inputs">
            <div>
              <label htmlFor="min-price">Minimum Price:</label>
              <TextInput name="min-price" onChange={handleMinPriceChange} value={minimumPrice} numberOnly={true} />
            </div>
            <div>
              <label htmlFor="max-price">Maximum Price:</label>
              <TextInput name="max-price" onChange={handleMaxPriceChange} value={maximumPrice} numberOnly={true} />
            </div>
          </div>

          <p>Date Initiated</p>
          <div className="deal-room-management__filter__filters__date-pickers">
            <div>
              <label htmlFor="initiated-from">From</label>
              <DatePicker
                name="initiated-from"
                value={initiatedFrom}
                onChange={(date) => setInitiatedFrom(date)}
                maxDate={initiatedTo}
              />
            </div>
            <div>
              <label htmlFor="initiated-to">To</label>
              <DatePicker
                name="initiated-to"
                value={initiatedTo}
                onChange={(date) => setInitiatedTo(date)}
                minDate={initiatedFrom}
              />
            </div>
          </div>
        </section>

        <div className="deal-room-management__filter__button-container">
          <Button onClick={() => applyFilters(0)}>Apply Filter</Button>
          {haveFiltersChanged() && <Button onClick={() => removeFilters()}>Remove Filter</Button>}
        </div>
      </section>

      <section className="deal-room-management__table">
        <section className="deal-room-management__table__header">
          <h1>Deals</h1>
        </section>

        <section className="deal-room-management__table__container">
          <Table
            data={contracts}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={applyFilters}
            onRowClick={handleRowClick}
            emptyMessage="No contracts found"
            hiddenColumns={['externalReference']}
          />
        </section>
      </section>
    </section>
  );
};

DealRoomManagement.propTypes = {
  contracts: PropTypes.arrayOf(PropTypes.shape({})),
  products: PropTypes.arrayOf(PropTypes.shape({})),
  facilitators: PropTypes.arrayOf(PropTypes.shape({})),
  initialFilters: PropTypes.shape({}).isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  retrieveContracts: PropTypes.func.isRequired,
};

DealRoomManagement.defaultProps = {
  contracts: [],
  products: [],
  facilitators: [],
  versions: [],
};

export default DealRoomManagement;
